import { useQuery } from '@apollo/client';
import { CircularProgress } from '@material-ui/core';
import { useUserInfo } from '@terragotech/gen5-shared-components';
import { DATETIME_TOKEN_CONVERSION, getDateTimeFormat } from '@terragotech/gen5-shared-utilities';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { ROW_HEIGHT } from '../../components/Common/Modals/TGOptionLazyModal/TGOptionModalRowRenderer';
import FluidStyledTableWithData from '../../components/StyledTable/FluidStyledTableWithDataManagement';
import UnauthorizedDisplay from '../../components/UnauthorizedDisplay';
import { Filters, FilterValue } from '../../contexts/AggregatesContext/useAssetsLoader';
import { useAssetsTableState } from '../../contexts/assetsTableStateContext';
import { Column } from '../../hooks/tableHooks/useColumns';
import { TableData } from '../../hooks/useTable';
import { TableDataContext } from '../../hooks/useTableData';
import { configState } from '../../recoil/atoms';
import { GET_DATA_EXPORT_MESSAGES } from './graphql';
import { useSharedStyles } from '../LogStyles';

interface DataExportMessage extends TableData {
  id: string;
  dateTime: Date;
  user: string;
  exportType: string;
  recordType: string;
  recordCount: number;
  appliedFilters: string;
  completionTime: number;
}

const HEADER_HEIGHT = 109;
const QUERY = GET_DATA_EXPORT_MESSAGES;
const NUM_ROWS = 40;

const DataExportTracking: React.FC = () => {
  const [afterCursor, setAfterCursor] = useState<string | null>(null);
  const [dataExportSort, setDataExportSort] = useState<string[]>(['NATURAL']);
  const [hasMore, setHasMore] = useState(true);
  const [messages, setMessages] = useState<DataExportMessage[]>([]);
  const [resetComplete, setResetComplete] = useState<boolean>(false);
  const [rowsRendered, setRowsRendered] = useState<number>(NUM_ROWS);
  const { defaultDateTimeFormat } = useRecoilValue(configState);
  const sharedClasses = useSharedStyles();
  const tableHeight = window.innerHeight - HEADER_HEIGHT - 60;
  const loadingMore = useRef(false);
  const { hasDataExportTrackingAccess } = useUserInfo();
  const { sortColumn, sortDirection } = useAssetsTableState();
  const { filters, partialFilters, setPartialFilters, setFilters, setFiltersApplied } = useContext(TableDataContext);

  useEffect(() => {
    setPartialFilters({});
    setFilters({});
    setFiltersApplied(false);
    setResetComplete(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: Column<DataExportMessage>[] = useMemo(
    () => [
      // { key: 'id', name: 'ID', resizable: false, sortable: false, width: 275 },
      {
        key: 'dateTime',
        name: 'Date/Time',
        dataType: 'DateTime',
        resizable: true,
        sortable: true,
        cellRenderer: ({ row }) => (
          <div>
            {row.dateTime &&
              moment(row.dateTime).format(
                getDateTimeFormat(
                  defaultDateTimeFormat?.dateFormatType,
                  defaultDateTimeFormat?.dateFormat,
                  defaultDateTimeFormat?.dateSeperator,
                  defaultDateTimeFormat?.timeFormat,
                  { tokenConversion: DATETIME_TOKEN_CONVERSION.MomentJS }
                )
              )}
          </div>
        ),
      },
      { key: 'user', name: 'User', resizable: true, sortable: true, dataType: 'String' },
      { key: 'exportType', name: 'Export Type', resizable: true, sortable: true, dataType: 'String' },
      { key: 'recordType', name: 'Record Type', resizable: true, sortable: true, dataType: 'String' },
      { key: 'recordCount', name: 'Number of Records', resizable: true, sortable: true, dataType: 'Number' },
      {
        key: 'appliedFilters',
        name: 'Applied Filters',
        resizable: true,
        sortable: true,
        dataType: 'String',
        filterRenderer: false,
      },
      {
        key: 'completionTime',
        name: 'Completion Time (s)',
        resizable: true,
        sortable: true,
        dataType: 'Number',
        cellRenderer: ({ row }) => <div>{row.completionTime != null ? row.completionTime / 1000 : ''}</div>,
      },
    ],
    [defaultDateTimeFormat]
  );

  const getCurrFilter = useMemo((): Filters => {
    if (!resetComplete) return { id: { isNull: false } };
    return {
      id: { isNull: false },
      ...Object.fromEntries(
        Object.entries(filters).reduce((acc, [x, y]) => {
          const filterColumn = columns?.find(column => column.key === x);

          if (!filterColumn?.dataType || filterColumn.dataType !== 'DateTime') {
            const value: FilterValue = {
              in: Array.isArray(y) ? y : [y],
            };
            acc.push([x, value]);
          }

          return acc;
        }, [] as [string, FilterValue][])
      ),
      and: Object.entries(filters).reduce((acc, [x, y]) => {
        const filterColumn = columns?.find(column => column.key === x);
        if (filterColumn?.dataType === 'DateTime' && Array.isArray(y) && y.length >= 4) {
          acc.push({ [x]: { greaterThanOrEqualTo: y[1] } });
          acc.push({ [x]: { lessThanOrEqualTo: y[3] } });
        }

        return acc;
      }, [] as Filters[]),
      ...Object.fromEntries(
        Object.entries(partialFilters).reduce((acc, [x, y]) => {
          const filterColumn = columns?.find(column => column.key === x);
          if (filterColumn?.dataType === 'String') {
            acc.push([x, { includesInsensitive: y as string }]);
          }

          return acc;
        }, [] as [string, FilterValue][])
      ),
    };
  }, [partialFilters, filters, columns, resetComplete]);

  const { loading, fetchMore } = useQuery(QUERY, {
    variables: {
      first: NUM_ROWS,
      after: null,
      filter: { id: { isNull: false } },
      orderBy: ['NATURAL'],
    },
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const edges = data?.dataExportTrackingsConnection?.edges || [];
      const newMessages = edges.map((edge: any) => {
        const { appliedFilters } = edge.node as {
          appliedFilters: JSON;
        };

        return {
          ...edge.node,
          appliedFilters: appliedFilters ? JSON.stringify(appliedFilters) : 'none',
        };
      });

      setMessages(newMessages);
      setAfterCursor(data?.dataExportTrackingsConnection?.pageInfo?.endCursor || null);
      setHasMore(data?.dataExportTrackingsConnection?.pageInfo?.hasNextPage || false);
    },
    onError: () => {},
  });

  useEffect(() => {
    if (!hasDataExportTrackingAccess) return;
    setAfterCursor(null);
    setMessages([]);
    setRowsRendered(NUM_ROWS);
    setHasMore(true);

    let currSort = dataExportSort;
    const sortingColumn = columns?.find(column => column.key === sortColumn);
    if (sortingColumn?.sortable) {
      const normalizedSortColumn = sortColumn?.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();
      const sortOutput =
        sortDirection === 'NONE' || !sortColumn ? 'NATURAL' : `${normalizedSortColumn}_${sortDirection}`;

      currSort = [sortOutput, 'ID_ASC'];
    }

    fetchMore({
      variables: {
        first: NUM_ROWS,
        after: null,
        filter: getCurrFilter,
        orderBy: currSort,
      },
      updateQuery: (_, { fetchMoreResult }) => {
        const edges = fetchMoreResult?.dataExportTrackingsConnection?.edges || [];
        const newMessages = edges.map((edge: any) => ({
          ...edge.node,
          appliedFilters: edge.node.appliedFilters ? JSON.stringify(edge.node.appliedFilters) : '',
        }));

        setMessages(newMessages);
        setAfterCursor(fetchMoreResult?.dataExportTrackingsConnection?.pageInfo?.endCursor || null);
        setHasMore(fetchMoreResult?.dataExportTrackingsConnection?.pageInfo?.hasNextPage || false);
        if (currSort !== dataExportSort) {
          setDataExportSort(currSort);
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, partialFilters, sortColumn, sortDirection]);

  const loadMoreData = useCallback(
    async (input: number) => {
      if (!hasMore || loading || loadingMore.current || !hasDataExportTrackingAccess) return;
      if (!(input + Math.floor(tableHeight / ROW_HEIGHT) >= rowsRendered - 5)) return;

      loadingMore.current = true;
      await fetchMore({
        variables: {
          first: NUM_ROWS,
          after: afterCursor,
          filter: getCurrFilter,
          orderBy: dataExportSort,
        },
        updateQuery: (_, { fetchMoreResult }) => {
          const edges = fetchMoreResult?.dataExportTrackingsConnection?.edges || [];
          const newMessages = edges.map((edge: any) => ({
            ...edge.node,
            appliedFilters: edge.node.appliedFilters ? JSON.stringify(edge.node.appliedFilters) : '',
          }));

          setMessages(prev => [...prev, ...newMessages]);
          setAfterCursor(fetchMoreResult?.dataExportTrackingsConnection?.pageInfo?.endCursor || null);
          setHasMore(fetchMoreResult?.dataExportTrackingsConnection?.pageInfo?.hasNextPage || false);
          setRowsRendered(prevRows => prevRows + NUM_ROWS);
          loadingMore.current = false;
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasMore, fetchMore, loading, rowsRendered, afterCursor]
  );

  return (
    <>
      {hasDataExportTrackingAccess ? (
        <div className={sharedClasses.root}>
          {loading && !messages.length ? (
            <div className={sharedClasses.loader}>
              <CircularProgress />
            </div>
          ) : (
            <FluidStyledTableWithData<DataExportMessage>
              columns={columns}
              data={messages}
              searchBar={() => <></>}
              searchBarHeight={0}
              onLoad={loadMoreData}
              height={tableHeight}
              disableSort
              schema={'log'}
              tableName="data_export_tracking"
            />
          )}
        </div>
      ) : (
        <UnauthorizedDisplay />
      )}
    </>
  );
};

export default DataExportTracking;
